// Story 048 — Leader election via BroadcastChannel.
//
// Multiple writer windows for the same DA must produce exactly ONE
// heartbeat per 15s (Spec 095 §"Leader-election traffic budget"). We
// elect a leader via a deterministic windowId tie-break: every window
// posts `claim:<windowId>` on join; each window keeps the set of seen
// peers; the leader is the one with the LOWEST windowId in the set.
//
// The protocol is intentionally simple — no Bully algorithm, no
// vector clocks. Wall-clock skew is irrelevant; we only need a stable
// total ordering of windowIds.
//
// Messages:
//   - { type: 'claim', windowId } — sent on join + every 5s heartbeat
//   - { type: 'leave', windowId } — sent on unmount (best-effort)
//
// The BroadcastChannel name includes the data-acquisition id so the
// election is per-DA. Windows on different DAs do not collide.
import { useEffect } from "react";
import { useLockStore } from "./lockStore.js";

const CLAIM_INTERVAL_MS = 5_000;
const PEER_TIMEOUT_MS = 12_000; // 2.4 claim intervals

interface ClaimMessage {
  type: "claim";
  windowId: string;
  at: number; // epoch ms
}

interface LeaveMessage {
  type: "leave";
  windowId: string;
}

type ChannelMessage = ClaimMessage | LeaveMessage;

export interface LockBroadcastChannelOptions {
  dataAcquisitionId: string;
  enabled: boolean;
  windowId: string;
  /** Test seam — defaults to globalThis.BroadcastChannel. */
  channelFactory?: (name: string) => BroadcastChannel | null;
}

/**
 * Pure (non-React) leader-election runtime. Exposed for unit tests
 * which need to drive the protocol without a React tree.
 */
export interface LeaderElectionRuntime {
  start(): void;
  stop(): void;
  /** Returns true iff this window currently believes it is leader. */
  isLeader(): boolean;
  /** For tests: simulate receiving a peer message. */
  receive(msg: ChannelMessage): void;
  /** For tests: snapshot the known peers. */
  peers(): ReadonlySet<string>;
}

export function createLeaderElection(args: {
  dataAcquisitionId: string;
  windowId: string;
  channelFactory?: (name: string) => BroadcastChannel | null;
  onLeaderChange: (isLeader: boolean) => void;
  onPeersChange?: (peers: ReadonlySet<string>) => void;
  now?: () => number;
}): LeaderElectionRuntime {
  const now = args.now ?? (() => Date.now());
  const peersWithSeen = new Map<string, number>();
  let leader = false;
  let channel: BroadcastChannel | null = null;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  const recomputeLeader = (): void => {
    // Sweep peers we haven't heard from in >PEER_TIMEOUT_MS.
    const t = now();
    for (const [peer, seen] of peersWithSeen) {
      if (peer === args.windowId) continue;
      if (t - seen > PEER_TIMEOUT_MS) peersWithSeen.delete(peer);
    }
    // Always include self.
    peersWithSeen.set(args.windowId, t);
    let lowest = args.windowId;
    for (const peer of peersWithSeen.keys()) {
      if (peer < lowest) lowest = peer;
    }
    const nextLeader = lowest === args.windowId;
    if (nextLeader !== leader) {
      leader = nextLeader;
      args.onLeaderChange(leader);
    }
    args.onPeersChange?.(new Set(peersWithSeen.keys()));
  };

  const send = (msg: ChannelMessage): void => {
    if (!channel) return;
    try {
      channel.postMessage(msg);
    } catch {
      /* swallow — closed channel during teardown */
    }
  };

  const tick = (): void => {
    send({ type: "claim", windowId: args.windowId, at: now() });
    recomputeLeader();
  };

  const handleMessage = (event: MessageEvent): void => {
    const data = event.data as ChannelMessage | null;
    if (!data || typeof data !== "object") return;
    if (data.type === "claim") {
      peersWithSeen.set(data.windowId, now());
      recomputeLeader();
      return;
    }
    // Only "leave" remains in the discriminated union.
    peersWithSeen.delete(data.windowId);
    recomputeLeader();
  };

  return {
    start(): void {
      if (stopped) return;
      const factory =
        args.channelFactory ??
        ((name) => {
          if (typeof BroadcastChannel === "undefined") return null;
          return new BroadcastChannel(name);
        });
      channel = factory("aero-lyse-lock-" + args.dataAcquisitionId);
      if (channel) {
        channel.addEventListener("message", handleMessage);
      }
      // Initial tick — claim immediately so the user doesn't wait
      // 5s for the first election outcome.
      tick();
      intervalId = setInterval(tick, CLAIM_INTERVAL_MS);
    },
    stop(): void {
      if (stopped) return;
      stopped = true;
      if (intervalId !== null) clearInterval(intervalId);
      intervalId = null;
      send({ type: "leave", windowId: args.windowId });
      if (channel) {
        channel.removeEventListener("message", handleMessage);
        try {
          channel.close();
        } catch {
          /* swallow */
        }
        channel = null;
      }
    },
    isLeader(): boolean {
      return leader;
    },
    receive(msg): void {
      handleMessage({ data: msg } as MessageEvent);
    },
    peers(): ReadonlySet<string> {
      return new Set(peersWithSeen.keys());
    },
  };
}

/**
 * React hook wrapper for the leader-election runtime. Mounts on
 * `dataAcquisitionId` change, tears down on unmount.
 */
export function useLockBroadcastChannel({
  dataAcquisitionId,
  enabled,
  windowId,
  channelFactory,
}: LockBroadcastChannelOptions): void {
  useEffect(() => {
    if (!enabled) return;
    const election = createLeaderElection({
      dataAcquisitionId,
      windowId,
      ...(channelFactory ? { channelFactory } : {}),
      onLeaderChange: (isLeader) => {
        useLockStore.getState().setLeader(isLeader);
      },
      onPeersChange: (peers) => {
        useLockStore.getState().setPeers(peers);
      },
    });
    election.start();
    return () => {
      election.stop();
    };
  }, [dataAcquisitionId, enabled, windowId, channelFactory]);
}
